<template>
  <v-card class="mb-8" elevation="0">
    <v-card-text>
      <div class="subtitle-1 font-weight-bold mb-4">
        <v-icon left>mdi-clock-outline</v-icon>
        {{ $t("select_time_limit") }}
      </div>

      <div>
        <!-- Nema limit -->
        <selectable-card
          value="no_limit"
          :current-value="config.timing.active"
          icon="mdi-infinity"
          :title="$t('no_time_limit')"
          :description="$t('limit_valid_indefinitely')"
          @select="config.timing.active = $event"
          :readonly="readonly"
        />

        <!-- Po danu -->
        <selectable-card
          value="day"
          :current-value="config.timing.active"
          icon="mdi-calendar-today"
          :title="$t('daily_limit')"
          :description="$t('limit_resets_daily')"
          @select="config.timing.active = $event"
          :readonly="readonly"
        >
          <daily-limit-fields
            :config="config.limit_definition"
            :active-type="config.limit_definition.active"
            :timing-settings="config.timing.settings"
            :readonly="readonly"
          />
        </selectable-card>

        <!-- Po smeni -->
        <selectable-card
          value="shift"
          :current-value="config.timing.active"
          icon="mdi-clock-time-four-outline"
          :title="$t('shift_limit')"
          :description="$t('limit_resets_every_shift')"
          @select="config.timing.active = $event"
          :readonly="readonly"
        >
          <shift-limit-fields
            :config="config.limit_definition"
            :active-type="config.limit_definition.active"
            @update-shift="updateShiftBehavior"
          :readonly="readonly"
          />
        </selectable-card>

        <!-- Po sedmici -->
        <selectable-card
          value="week"
          :current-value="config.timing.active"
          icon="mdi-calendar-week"
          :title="$t('weekly_limit')"
          :description="$t('limit_resets_weekly')"
          @select="config.timing.active = $event"
          :readonly="readonly"
        >
          <weekly-limit-fields
            :config="config.limit_definition"
            :active-type="config.limit_definition.active"
            :timing-settings="config.timing.settings"
            :readonly="readonly"
          />
        </selectable-card>

        <!-- Po mesecu -->
        <selectable-card
          value="month"
          :current-value="config.timing.active"
          icon="mdi-calendar-month"
          :title="$t('monthly_limit')"
          :description="$t('limit_resets_monthly')"
          @select="config.timing.active = $event"
          :readonly="readonly"
        >
          <monthly-limit-fields
            :config="config.limit_definition"
            :active-type="config.limit_definition.active"
            :timing-settings="config.timing.settings"
            :readonly="readonly"
          />
        </selectable-card>

        <!-- Prilagođeni period -->
        <selectable-card
          value="custom"
          :current-value="config.timing.active"
          icon="mdi-calendar-range"
          :title="$t('custom_period')"
          :description="$t('define_own_time_period')"
          :last="true"
          @select="config.timing.active = $event"
          :readonly="readonly"
        >
          <custom-period-limit-fields
            :config="config.limit_definition"
            :active-type="config.limit_definition.active"
            :timing-settings="config.timing.settings"
            :readonly="readonly"
          />
        </selectable-card>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import SelectableCard from "../cards/SelectableCard.vue";
import DailyLimitFields from "../fields/DailyLimitFields.vue";
import ShiftLimitFields from "../fields/ShiftLimitFields.vue";
import WeeklyLimitFields from "../fields/WeeklyLimitFields.vue";
import MonthlyLimitFields from "../fields/MonthlyLimitFields.vue";
import CustomPeriodLimitFields from "../fields/CustomPeriodLimitFields.vue";

export default {
  name: "TimeLimitSelector",
  components: {
    SelectableCard,
    DailyLimitFields,
    ShiftLimitFields,
    WeeklyLimitFields,
    MonthlyLimitFields,
    CustomPeriodLimitFields,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
    limitId: {
      type: [Number, String],
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  created() {
    this.ensureTimingSettings();
  },
  methods: {
    ensureTimingSettings() {
      if (!this.config?.timing) return;
      if (!this.config.timing.settings) {
        this.$set(this.config.timing, "settings", {
          reset_time: null,
          reset_day: null,
          reset_month: null,
          start_date: null,
          end_date: null,
          week_start: null,
        });
      }
    },
    updateShiftBehavior(value) {
      const settings = this.config.limit_definition.settings;
      settings.apply_to_all_shifts = value;
      if (!value) {
        this.initializeShiftLimits(settings, this.config.limit_definition.active);
      }
    },
    initializeShiftLimits(settings, activeType) {
      if (!settings.shift_limits) {
        this.$set(settings, "shift_limits", {
          quantity_in_period: [null, null, null, null],
          max_orders: [null, null, null, null],
          max_order_value: [null, null, null, null],
          max_amount: [null, null, null, null],
          limit_amount: [null, null, null, null],
        });
      }

      const fill = (key, value) => {
        if (!Array.isArray(settings.shift_limits[key])) {
          this.$set(settings.shift_limits, key, [null, null, null, null]);
        }
        settings.shift_limits[key] = [value, value, value, value];
      };

      if (activeType === "voucher") {
        fill("quantity_in_period", settings.quantity_in_period);
      }

      if (activeType === "order") {
        if (settings.limit_by_quantity) {
          fill("max_orders", settings.max_orders);
        }
        if (settings.limit_by_value) {
          fill("max_order_value", settings.max_order_value);
        }
      }

      if (activeType === "credit") {
        fill("max_amount", settings.max_amount);
      }

      if (activeType === "money") {
        fill("limit_amount", settings.limit_amount);
      }
    },
  },
};
</script>
